import { toTestType } from '@risksmart-app/domain/src/types/consts/test-type';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import TestResultForm from 'src/pages/controls/update/forms/TestResultForm';
import type { TestResultFormFieldsData } from 'src/pages/controls/update/forms/testResultSchema';
import { defaultValues } from 'src/pages/controls/update/forms/testResultSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import type { UserOption } from 'src/schemas/global';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertControlTestOversightResult } from '@/hooks/mutations/oversight-result/useInsertControlTestOversightResult';
import { useUpdateControlTestOversightResult } from '@/hooks/mutations/oversight-result/useUpdateControlTestOversightResult';
import { useGetControlTestOversightResultById } from '@/hooks/queries';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { oversightAssessmentResultsUrl } from '@/utils/urls';
type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  parentId: string;
  controlTestId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (isSaved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  controlIds?: string[];
  header?: string;
  isModalForm?: boolean;
  showAssessmentSelector?: boolean;
};

const ConnectedControlTestOversightResultForm: FC<Props> = (props) => {
  const Id = props.id;
  const { data, loading: loadingOversightTestResult } =
    useGetControlTestOversightResultById({
      queryArgs: { id: Id! },
      shouldSkip: !Id,
    });
  const testResult = data?.control_test_oversight_result[0];
  const { updateFiles } = useMultiParentFileUpdate();

  const { insertControlTestOversightResult } =
    useInsertControlTestOversightResult();
  const { updateControlTestOversightResult } =
    useUpdateControlTestOversightResult();

  const onSave = async (data: TestResultFormFieldsData) => {
    const { files, ...rest } = data;
    let resultIds = Id ? [Id] : [];
    const oversightAssessmentId =
      props.parentId || rest.OversightAssessmentId || undefined;
    if (testResult) {
      const {
        ParentControlIds: _,
        OversightAssessmentId: __,
        ...updateData
      } = rest;
      const data = await updateControlTestOversightResult({
        ...updateData,
        OriginalTimestamp: testResult.ModifiedAtTimestamp,
        Id: testResult.Id,
        ParentControlId: testResult.ParentControlId,
        CustomAttributeData: rest.CustomAttributeData || undefined,
        Submitter: rest.Submitter.value,
      });
      if (!data?.updateControlTestOversightResultApi?.Id) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else {
      if (!oversightAssessmentId) {
        throw new Error('An oversight assessment is required');
      }
      const result = await insertControlTestOversightResult({
        OversightAssessmentId: oversightAssessmentId,
        ControlIds: rest.ParentControlIds.map((p) => p.value),
        Title: rest.Title,
        TestType: rest.TestType,
        Description: rest.Description ?? '',
        DesignEffectiveness: rest.DesignEffectiveness,
        PerformanceEffectiveness: rest.PerformanceEffectiveness,
        OverallEffectiveness: rest.OverallEffectiveness,
        Submitter: rest.Submitter.value,
        TestDate: rest.TestDate,
        CustomAttributeData: rest.CustomAttributeData ?? null,
      });
      if (!result.insertChildControlTestOversightResult?.Ids?.length) {
        throw new Error('Id not found');
      }
      resultIds = result.insertChildControlTestOversightResult.Ids;
    }
    const savedId = resultIds[0];
    if (!savedId) {
      throw new Error('Id not found');
    }

    const createdEntity = !testResult
      ? {
          id: savedId,
          destination: oversightAssessmentResultsUrl(oversightAssessmentId),
        }
      : undefined;
    const saveFiles = () =>
      updateFiles({
        parentType: Parent_Type_Enum.ControlTestOversightResult,
        parentIds: resultIds,
        originalFiles: testResult?.files.map((f) => f.file),
        selectedFiles: files,
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return {
      savedIds: resultIds,
      createdEntity,
      successNavigation:
        props.navigateToResults && oversightAssessmentId
          ? {
              destination: oversightAssessmentResultsUrl(oversightAssessmentId),
            }
          : undefined,
    };
  };

  const onDismiss = (isSaved: boolean) => {
    props.onDismiss?.(isSaved);
  };

  return (
    <div className={'pb-5'}>
      <TestResultForm
        {...props}
        renderTemplate={(renderProps) =>
          props.isModalForm ? (
            <ModalBodyWrapper {...renderProps} />
          ) : (
            <PageWrapper {...renderProps} />
          )
        }
        defaultValues={{
          ...defaultValues,
        }}
        assessmentMode={'oversight_assessment'}
        values={{
          ...defaultValues,
          ...testResult,
          TestType: toTestType(testResult?.TestType),
          files: testResult?.files.map((rf) => rf.file) ?? [],
          Submitter: testResult?.Submitter
            ? { value: testResult.Submitter, type: 'user' }
            : (undefined as unknown as UserOption),
          ParentControlIds: testResult
            ? [{ value: testResult.ParentControlId }]
            : props.controlIds
              ? props.controlIds.map((c) => ({
                  value: c,
                }))
              : [],
        }}
        onSave={onSave}
        onDismiss={onDismiss}
        readOnly={props.readonly || loadingOversightTestResult}
        disableControlSelect={props.id !== undefined}
      />
    </div>
  );
};

export default ConnectedControlTestOversightResultForm;
